import React from "react";
import ReactDOM from "react-dom/client";
import r2wc from "@r2wc/react-to-web-component";
import { QueryClientProvider } from "@tanstack/react-query";
import { getQueryClient } from "./lib/query-client";
import { BookNowButton } from "./components/BookNowButton";
import type { BookNowButtonProps } from "./components/BookNowButton";
import { CheckoutOverlay } from "./components/CheckoutOverlay";
// CheckoutOverlayProps is used by the r2wc definition
import type { CheckoutOverlayProps as _CheckoutOverlayProps } from "./components/CheckoutOverlay";
import { Timetable, TimetableOverlay } from "./components/Timetable";
import type {
  TimetableProps,
  TimetableOverlayProps,
} from "./components/Timetable";
import { GiftcardBuyNowButton } from "./components/GiftcardBuyNowButton";
import type { GiftcardBuyNowButtonProps } from "./components/GiftcardBuyNowButton";
import { CalendarVisibility } from "./components/CalendarVisibility";
import type { CalendarVisibilityProps } from "./components/CalendarVisibility";
import { Basket } from "./components/Basket";
import type { BasketProps } from "./components/Basket";
import { BasketCheckoutOverlay } from "./components/BasketCheckoutOverlay";
import type { BasketCheckoutOverlayProps as _BasketCheckoutOverlayProps } from "./components/BasketCheckoutOverlay";
import { GiftcardCheckoutOverlay } from "./components/GiftcardCheckoutOverlay";
import type { GiftcardCheckoutOverlayProps as _GiftcardCheckoutOverlayProps } from "./components/GiftcardCheckoutOverlay";
import { Chat } from "@baluu/chat-widget";
import type { ChatProps } from "@baluu/chat-widget";
import { loadAndApplyThemeOverrides, hasCachedTheme } from "./lib/theme";
import { WhatsOn } from "./components/WhatsOn/WhatsOn";
import type { WhatsOnProps } from "./components/WhatsOn/WhatsOn";
import { LoginButton } from "./components/LoginButton";
import type { LoginButtonProps } from "./components/LoginButton";
import { Profile, ProfileOverlay } from "./components/Profile";
import type { ProfileProps, ProfileOverlayProps } from "./components/Profile";
import { Booking } from "./components/Booking";
import type { BookingProps } from "./components/Booking";
import { PassCheckoutOverlay } from "./components/PassCheckoutOverlay";
import { MembershipCheckoutOverlay } from "./components/MembershipCheckoutOverlay";
import { ProductCheckoutOverlay } from "./components/ProductCheckoutOverlay";
import { openPurchaseOverlay, resumeMembershipReturn } from "./lib/purchase-overlay";
import { openBasket } from "./lib/ensure-basket-element";
import { resumeBasketPaymentReturn, type BasketPaymentReturnProp } from "./lib/payment-return";
import { openBasketCheckout } from "./lib/open-basket-checkout";
// Every element with a `primary-color` attribute draws in its host's brand
// when the attribute is absent (the page's theme, or Baluu's default).
import { withHostBrand } from "./lib/host-brand";

// Import styles - will be compiled by Tailwind and available via ?inline
// Note: We also check window.__BALUU_WIDGETS_CSS__ as fallback from vite-plugin-css-injected-by-js
import cssText from "./styles/globals.css?inline";

// Flag to track if document-level rules have been injected into document head
let documentRulesInjected = false;

/**
 * Lift the at-rules that browsers ignore inside a shadow root into the host
 * document's head, once:
 *
 * - `@font-face`: font declarations in Shadow DOM don't load in all browsers.
 * - `@property`: registered custom properties only take effect at document
 *   level. Tailwind v4 utilities rely on them for their defaults — `.border`
 *   is `border-style: var(--tw-border-style)` with `solid` coming from
 *   `@property --tw-border-style` — so inside our shadow roots the variable was
 *   undefined and every bordered input rendered with no border (an attendee
 *   field showed as a bare label with nothing to type into). Registration is
 *   document-wide, so one copy in the head fixes every widget's shadow tree,
 *   and it can't style the host page: it only defines the variables' initial
 *   values. A host page on Tailwind v4 registers identical definitions.
 */
function injectDocumentLevelRules() {
  if (documentRulesInjected || typeof document === "undefined") return;
  documentRulesInjected = true;

  const css =
    cssText ||
    (window as unknown as { __BALUU_WIDGETS_CSS__?: string })
      .__BALUU_WIDGETS_CSS__ ||
    "";

  if (!css) return;

  const inject = (id: string, rules: RegExpMatchArray | null) => {
    if (!rules || rules.length === 0) return;
    const style = document.createElement("style");
    style.id = id;
    style.textContent = rules.join("\n");
    document.head.appendChild(style);
  };
  inject("baluu-widgets-fonts", css.match(/@font-face\s*\{[^}]+\}/g));
  inject("baluu-widgets-properties", css.match(/@property\s+--[\w-]+\s*\{[^}]*\}/g));
}

// Wrapper component that provides React Query context
function BookNowButtonWithProviders(props: BookNowButtonProps) {
  const queryClient = getQueryClient();

  return React.createElement(
    QueryClientProvider,
    { client: queryClient },
    React.createElement(BookNowButton, props),
  );
}

// Create the web component with Shadow DOM for style isolation
const BookNowWebComponent = r2wc(BookNowButtonWithProviders, {
  props: {
    listingId: "string",
    businessId: "string",
    businessSlug: "string",
    apiUrl: "string",
    buttonText: "string",
    soldOutText: "string",
    buttonClassName: "string",
    buttonStyle: "string",
    theme: "string",
    widgetBaseUrl: "string",
    gtagConversionId: "string",
    gtagConversionLabel: "string",
  },
  shadow: "open",
});

// Create the checkout overlay web component
// NOTE: Shadow DOM is used for CSS isolation. Stripe Elements are rendered via portal
// outside Shadow DOM to avoid "Element didn't mount normally" errors.
const CheckoutOverlayWebComponent = r2wc(withHostBrand(CheckoutOverlay), {
  props: {
    listingId: "string",
    businessId: "string",
    businessSlug: "string",
    apiUrl: "string",
    primaryColor: "string",
    theme: "string",
    widgetBaseUrl: "string",
    initialSessionId: "string",
    initialBookingType: "string",
    // A full date's waiting list, opened straight away.
    waitingListSessionId: "string",
    // A waiting-list offer being claimed (`offer-ref`, its hold id).
    offerRef: "string",
    // The surface's basket ("on" / "page"; none: one thing at a time).
    basket: "string",
  },
  shadow: "open",
});

// Wrapper component that provides React Query context for Timetable
function TimetableWithProviders(props: TimetableProps) {
  const queryClient = getQueryClient();

  return React.createElement(
    QueryClientProvider,
    { client: queryClient },
    React.createElement(Timetable, props),
  );
}

// Create the timetable web component
const TimetableWebComponent = r2wc(withHostBrand(TimetableWithProviders), {
  props: {
    businessSlug: "string",
    apiUrl: "string",
    primaryColor: "string",
    theme: "string",
    gtagConversionId: "string",
    gtagConversionLabel: "string",
    mode: "string",
    buttonText: "string",
    buttonClassName: "string",
    buttonStyle: "string",
    courseIds: "string",
    tagIds: "string",
  },
  shadow: "open",
});

// Wrapper component that provides React Query context for WhatsOn
function WhatsOnWithProviders(props: WhatsOnProps) {
  const queryClient = getQueryClient();

  return React.createElement(
    QueryClientProvider,
    { client: queryClient },
    React.createElement(WhatsOn, props),
  );
}

// Create the What's On web component
const WhatsOnWebComponent = r2wc(withHostBrand(WhatsOnWithProviders), {
  props: {
    school: "string",
    apiUrl: "string",
    primaryColor: "string",
    gtagConversionId: "string",
    gtagConversionLabel: "string",
  },
  shadow: "open",
});

// Wrapper component that provides React Query context for TimetableOverlay
function TimetableOverlayWithProviders(props: TimetableOverlayProps) {
  const queryClient = getQueryClient();

  return React.createElement(
    QueryClientProvider,
    { client: queryClient },
    React.createElement(TimetableOverlay, props),
  );
}

// Create the timetable overlay web component (used internally by popup-mode <baluu-timetable>)
const TimetableOverlayWebComponent = r2wc(withHostBrand(TimetableOverlayWithProviders), {
  props: {
    businessSlug: "string",
    apiUrl: "string",
    primaryColor: "string",
    theme: "string",
    gtagConversionId: "string",
    gtagConversionLabel: "string",
  },
  shadow: "open",
});

// Wrapper component that provides React Query context for GiftcardBuyNowButton
function GiftcardBuyNowButtonWithProviders(props: GiftcardBuyNowButtonProps) {
  const queryClient = getQueryClient();

  return React.createElement(
    QueryClientProvider,
    { client: queryClient },
    React.createElement(GiftcardBuyNowButton, props),
  );
}

// Create the giftcard buy now button web component
const GiftcardBuyNowWebComponent = r2wc(withHostBrand(GiftcardBuyNowButtonWithProviders), {
  props: {
    businessId: "string",
    businessSlug: "string",
    apiUrl: "string",
    mode: "string",
    buttonText: "string",
    buttonClassName: "string",
    buttonStyle: "string",
    theme: "string",
    primaryColor: "string",
    widgetBaseUrl: "string",
    gtagConversionId: "string",
    gtagConversionLabel: "string",
  },
  shadow: "open",
});

// Create the giftcard checkout overlay web component
// NOTE: Shadow DOM is used for CSS isolation. Stripe Elements are rendered via portal
// outside Shadow DOM to avoid "Element didn't mount normally" errors.
const GiftcardCheckoutOverlayWebComponent = r2wc(withHostBrand(GiftcardCheckoutOverlay), {
  props: {
    businessId: "string",
    businessSlug: "string",
    apiUrl: "string",
    primaryColor: "string",
    theme: "string",
    widgetBaseUrl: "string",
    mode: "string",
    basket: "string",
  },
  shadow: "open",
});

// Chat widget — floating booking-assistant bubble. Calls the shared agent
// brain via /v1/chat/:schoolSlug/* (the same runAgent that powers WhatsApp).
const ChatWebComponent = r2wc(Chat, {
  props: {
    businessSlug: "string",
    apiUrl: "string",
    primaryColor: "string",
    position: "string",
    title: "string",
  },
  shadow: "open",
});

// Login button — signed-out "Sign in" (opens CustomerAuthDialog) or signed-in
// "Hi, {name}" with a My profile / Sign out dropdown.
const LoginWebComponent = r2wc(withHostBrand(LoginButton), {
  props: {
    businessId: "string",
    businessSlug: "string",
    apiUrl: "string",
    widgetBaseUrl: "string",
    theme: "string",
    primaryColor: "string",
    label: "string",
    profileUrl: "string",
  },
  shadow: "open",
});

// Wrapper component that provides React Query context for Profile —
// useProfileData (Profile/useProfileData.ts) is built on useQuery/useMutation.
function ProfileWithProviders(props: ProfileProps) {
  const queryClient = getQueryClient();

  return React.createElement(
    QueryClientProvider,
    { client: queryClient },
    React.createElement(Profile, props),
  );
}

// Inline profile widget: signed-out sign-in card, or the signed-in 6-tab
// profile (Overview/Bundles/Memberships/Purchases/Activity/Settings).
const ProfileWebComponent = r2wc(withHostBrand(ProfileWithProviders), {
  props: {
    businessId: "string",
    businessSlug: "string",
    apiUrl: "string",
    widgetBaseUrl: "string",
    theme: "string",
    primaryColor: "string",
    initialTab: "string",
  },
  shadow: "open",
});

// Wrapper component that provides React Query context for ProfileOverlay
// (ProfileOverlay renders <Profile>, which itself needs the query client).
function ProfileOverlayWithProviders(props: ProfileOverlayProps) {
  const queryClient = getQueryClient();

  return React.createElement(
    QueryClientProvider,
    { client: queryClient },
    React.createElement(ProfileOverlay, props),
  );
}

// Full-screen profile overlay — opened by LoginButton.tsx's "My profile"
// menu item (creates a bare <baluu-profile-overlay> and appends it to
// document.body) or programmatically the same way.
const ProfileOverlayWebComponent = r2wc(withHostBrand(ProfileOverlayWithProviders), {
  props: {
    businessId: "string",
    businessSlug: "string",
    apiUrl: "string",
    widgetBaseUrl: "string",
    theme: "string",
    primaryColor: "string",
  },
  shadow: "open",
});

// Type for custom element with lifecycle methods
type CustomElementWithLifecycle = HTMLElement & {
  connectedCallback?(): void;
};

// Helper function to inject CSS into shadow DOM
// Automatically transforms :root to :host so CSS variables cascade properly
function injectCssIntoShadow(element: HTMLElement) {
  const shadowRoot = (element as HTMLElement & { shadowRoot: ShadowRoot })
    .shadowRoot;
  if (shadowRoot) {
    // Try to get CSS from inline import first, then fallback to global variable
    let css =
      cssText ||
      (window as unknown as { __BALUU_WIDGETS_CSS__?: string })
        .__BALUU_WIDGETS_CSS__ ||
      "";

    if (css) {
      // Transform :root to :host for Shadow DOM compatibility
      // This ensures all CSS variables defined on :root cascade properly
      // inside the Shadow DOM (since :root in Shadow DOM refers to document root)
      css = css.replace(/:root\s*\{/g, ":host {");

      const style = document.createElement("style");
      style.textContent = css;
      shadowRoot.prepend(style);
    }
  }
}

// How long a first-time visitor's widget stays hidden waiting for the theme
// before revealing with default styling anyway.
const FIRST_VISIT_REVEAL_TIMEOUT_MS = 400;

/**
 * Load and apply the school's theme overrides (async, cached).
 *
 * On the very first visit (no cached theme) the host element is kept
 * invisible until the theme has applied — capped at
 * FIRST_VISIT_REVEAL_TIMEOUT_MS — so the widget's first paint is in the
 * school's brand instead of flashing the default palette. Repeat visitors
 * have a cached theme, which loadAndApplyThemeOverrides applies before the
 * first paint, so they skip the hold entirely.
 *
 * Supports both business-slug and business-id for backwards compatibility.
 */
function loadThemeWithFlashGuard(element: HTMLElement, logLabel: string) {
  const businessIdentifier =
    element.getAttribute("business-slug") || element.getAttribute("business-id");
  const apiUrl = element.getAttribute("api-url") || undefined;
  const shadowRoot = (element as HTMLElement & { shadowRoot: ShadowRoot | null })
    .shadowRoot;

  if (!businessIdentifier || !shadowRoot) return;

  let reveal: (() => void) | undefined;
  if (!hasCachedTheme(businessIdentifier)) {
    // visibility (not display) so the host page's layout doesn't jump
    element.style.visibility = "hidden";
    let revealed = false;
    reveal = () => {
      if (revealed) return;
      revealed = true;
      element.style.visibility = "";
    };
    setTimeout(reveal, FIRST_VISIT_REVEAL_TIMEOUT_MS);
  }

  loadAndApplyThemeOverrides(businessIdentifier, shadowRoot, apiUrl)
    .catch((err) => {
      console.warn(`[${logLabel}] Failed to load theme overrides:`, err);
    })
    .finally(() => reveal?.());
}

// Custom element that injects CSS into shadow DOM and loads theme overrides
class BaluuBookNow extends (BookNowWebComponent as unknown as {
  new (): CustomElementWithLifecycle;
}) {
  connectedCallback() {
    try {
      // Call parent's connectedCallback to create shadowRoot and mount React
      const parent = Object.getPrototypeOf(Object.getPrototypeOf(this));
      if (parent?.connectedCallback) {
        parent.connectedCallback.call(this);
      }

      // Inject fonts into document head (only once, for all components)
      injectDocumentLevelRules();

      // Inject CSS into shadow DOM
      injectCssIntoShadow(this);

      // Fetch and apply theme overrides (holds first paint on first visit)
      loadThemeWithFlashGuard(this, "Baluu Book Now");
    } catch (error) {
      console.error("[Baluu Book Now] Error in connectedCallback:", error);
      // Fallback: show error in shadowRoot if exists, otherwise light DOM
      const container = this.shadowRoot || this;
      const errorDiv = document.createElement("div");
      errorDiv.style.cssText =
        "padding: 20px; text-align: center; color: #dc2626; background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; margin: 20px;";
      errorDiv.innerHTML = `
        <strong>Widget Error</strong><br>
        <small>Failed to initialize. Please refresh the page or contact support.</small>
      `;
      container.appendChild(errorDiv);
    }
  }
}

// Checkout overlay web component with CSS injection and full-screen overlay styling
// Uses Shadow DOM for CSS isolation, with Stripe Elements rendered via portal outside Shadow DOM
class BaluuCheckoutOverlay extends (CheckoutOverlayWebComponent as unknown as {
  new (): CustomElementWithLifecycle;
}) {
  connectedCallback() {
    try {
      // Call parent's connectedCallback to create shadowRoot and mount React
      const parent = Object.getPrototypeOf(Object.getPrototypeOf(this));
      if (parent?.connectedCallback) {
        parent.connectedCallback.call(this);
      }

      // Inject fonts into document head (only once, for all components)
      injectDocumentLevelRules();

      // Style the host element as a full-screen fixed overlay
      this.style.cssText = `
        display: block;
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        width: 100%;
        height: 100%;
        z-index: 9999;
      `;

      // Inject CSS into shadow DOM for style isolation
      injectCssIntoShadow(this);

      // Fetch and apply theme overrides (holds first paint on first visit)
      loadThemeWithFlashGuard(this, "Baluu Checkout Overlay");
    } catch (error) {
      console.error(
        "[Baluu Checkout Overlay] Error in connectedCallback:",
        error,
      );
      // Fallback: show error in shadowRoot if exists, otherwise light DOM
      const container = this.shadowRoot || this;
      const errorDiv = document.createElement("div");
      errorDiv.style.cssText =
        "padding: 20px; text-align: center; color: #dc2626; background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; margin: 20px;";
      errorDiv.innerHTML = `
        <strong>Widget Error</strong><br>
        <small>Failed to initialize. Please refresh the page or contact support.</small>
      `;
      container.appendChild(errorDiv);
    }
  }
}

// Timetable web component with CSS injection and dynamic theme loading
class BaluuTimetable extends (TimetableWebComponent as unknown as {
  new (): CustomElementWithLifecycle;
}) {
  connectedCallback() {
    try {
      // Call parent's connectedCallback to create shadowRoot and mount React
      const parent = Object.getPrototypeOf(Object.getPrototypeOf(this));
      if (parent?.connectedCallback) {
        parent.connectedCallback.call(this);
      }

      // Inject fonts into document head (only once, for all components)
      injectDocumentLevelRules();

      // Inject CSS into shadow DOM (includes default theme from globals.css)
      injectCssIntoShadow(this);

      // Fetch and apply theme OVERRIDES (globals.css already sets defaults;
      // holds first paint on first visit)
      loadThemeWithFlashGuard(this, "Baluu Timetable");
    } catch (error) {
      console.error("[Baluu Timetable] Error in connectedCallback:", error);
      // Fallback: show error in shadowRoot if exists, otherwise light DOM
      const container = this.shadowRoot || this;
      const errorDiv = document.createElement("div");
      errorDiv.style.cssText =
        "padding: 20px; text-align: center; color: #dc2626; background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; margin: 20px;";
      errorDiv.innerHTML = `
        <strong>Widget Error</strong><br>
        <small>Failed to initialize. Please refresh the page or contact support.</small>
      `;
      container.appendChild(errorDiv);
    }
  }
}

// What's On web component — bespoke Botanical Boys theme, so no theme overrides
class BaluuWhatsOn extends (WhatsOnWebComponent as unknown as {
  new (): CustomElementWithLifecycle;
}) {
  connectedCallback() {
    try {
      // Call parent's connectedCallback to create shadowRoot and mount React
      const parent = Object.getPrototypeOf(Object.getPrototypeOf(this));
      if (parent?.connectedCallback) {
        parent.connectedCallback.call(this);
      }

      // Inject fonts into document head (only once, for all components)
      injectDocumentLevelRules();

      // Inject CSS into shadow DOM
      injectCssIntoShadow(this);

      // Deliberately no loadThemeWithFlashGuard/loadAndApplyThemeOverrides call —
      // this widget has a bespoke theme that must not be overridden by dashboard theming.
    } catch (error) {
      console.error("[Baluu WhatsOn] Error in connectedCallback:", error);
      // Fallback: show error in shadowRoot if exists, otherwise light DOM
      const container = this.shadowRoot || this;
      const errorDiv = document.createElement("div");
      errorDiv.style.cssText =
        "padding: 20px; text-align: center; color: #dc2626; background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; margin: 20px;";
      errorDiv.innerHTML = `
        <strong>Widget Error</strong><br>
        <small>Failed to initialize. Please refresh the page or contact support.</small>
      `;
      container.appendChild(errorDiv);
    }
  }
}

// Timetable overlay web component with CSS injection — full-screen fixed overlay
class BaluuTimetableOverlay extends (TimetableOverlayWebComponent as unknown as {
  new (): CustomElementWithLifecycle;
}) {
  connectedCallback() {
    try {
      // Call parent's connectedCallback to create shadowRoot and mount React
      const parent = Object.getPrototypeOf(Object.getPrototypeOf(this));
      if (parent?.connectedCallback) {
        parent.connectedCallback.call(this);
      }

      // Inject fonts into document head (only once, for all components)
      injectDocumentLevelRules();

      // Style the host element as a full-screen fixed overlay
      this.style.cssText = `
        display: block;
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        width: 100%;
        height: 100%;
        z-index: 9998;
      `;

      // Inject CSS into shadow DOM for style isolation
      injectCssIntoShadow(this);

      // Fetch and apply theme overrides (holds first paint on first visit)
      loadThemeWithFlashGuard(this, "Baluu Timetable Overlay");
    } catch (error) {
      console.error(
        "[Baluu Timetable Overlay] Error in connectedCallback:",
        error,
      );
      const container = this.shadowRoot || this;
      const errorDiv = document.createElement("div");
      errorDiv.style.cssText =
        "padding: 20px; text-align: center; color: #dc2626; background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; margin: 20px;";
      errorDiv.innerHTML = `
        <strong>Widget Error</strong><br>
        <small>Failed to initialize. Please refresh the page or contact support.</small>
      `;
      container.appendChild(errorDiv);
    }
  }
}

// Custom element for Giftcard Buy Now Button with CSS injection and theme loading
class BaluuGiftcardBuyNow extends (GiftcardBuyNowWebComponent as unknown as {
  new (): CustomElementWithLifecycle;
}) {
  connectedCallback() {
    try {
      // Call parent's connectedCallback to create shadowRoot and mount React
      const parent = Object.getPrototypeOf(Object.getPrototypeOf(this));
      if (parent?.connectedCallback) {
        parent.connectedCallback.call(this);
      }

      // Inject fonts into document head (only once, for all components)
      injectDocumentLevelRules();

      // Inject CSS into shadow DOM
      injectCssIntoShadow(this);

      // Fetch and apply theme overrides (holds first paint on first visit)
      loadThemeWithFlashGuard(this, "Baluu Giftcard Buy Now");
    } catch (error) {
      console.error("[Baluu Giftcard Buy Now] Error in connectedCallback:", error);
      const container = this.shadowRoot || this;
      const errorDiv = document.createElement("div");
      errorDiv.style.cssText =
        "padding: 20px; text-align: center; color: #dc2626; background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; margin: 20px;";
      errorDiv.innerHTML = `
        <strong>Widget Error</strong><br>
        <small>Failed to initialize. Please refresh the page or contact support.</small>
      `;
      container.appendChild(errorDiv);
    }
  }
}

// Giftcard Checkout Overlay web component with CSS injection and optional full-screen overlay styling
// Uses Shadow DOM for CSS isolation, with Stripe Elements rendered via portal outside Shadow DOM
class BaluuGiftcardCheckoutOverlay extends (GiftcardCheckoutOverlayWebComponent as unknown as {
  new (): CustomElementWithLifecycle;
}) {
  connectedCallback() {
    try {
      // Call parent's connectedCallback to create shadowRoot and mount React
      const parent = Object.getPrototypeOf(Object.getPrototypeOf(this));
      if (parent?.connectedCallback) {
        parent.connectedCallback.call(this);
      }

      // Inject fonts into document head (only once, for all components)
      injectDocumentLevelRules();

      // Check display mode - only apply overlay styles for popup mode
      const mode = this.getAttribute("mode") || "popup";
      if (mode === "popup") {
        // Style the host element as a full-screen fixed overlay
        this.style.cssText = `
          display: block;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          width: 100%;
          height: 100%;
          z-index: 9999;
        `;
      } else {
        // Inline mode - display as block element
        this.style.cssText = `
          display: block;
          width: 100%;
        `;
      }

      // Inject CSS into shadow DOM for style isolation
      injectCssIntoShadow(this);

      // Fetch and apply theme overrides (holds first paint on first visit)
      loadThemeWithFlashGuard(this, "Baluu Giftcard Checkout Overlay");
    } catch (error) {
      console.error(
        "[Baluu Giftcard Checkout Overlay] Error in connectedCallback:",
        error,
      );
      const container = this.shadowRoot || this;
      const errorDiv = document.createElement("div");
      errorDiv.style.cssText =
        "padding: 20px; text-align: center; color: #dc2626; background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; margin: 20px;";
      errorDiv.innerHTML = `
        <strong>Widget Error</strong><br>
        <small>Failed to initialize. Please refresh the page or contact support.</small>
      `;
      container.appendChild(errorDiv);
    }
  }
}

// Wrapper component that provides React Query context for CalendarVisibility
function CalendarVisibilityWithProviders(props: CalendarVisibilityProps) {
  const queryClient = getQueryClient();

  return React.createElement(
    QueryClientProvider,
    { client: queryClient },
    React.createElement(CalendarVisibility, props),
  );
}

// Create the calendar visibility web component
const CalendarVisibilityWebComponent = r2wc(withHostBrand(CalendarVisibilityWithProviders), {
  props: {
    listingIds: "string",
    businessId: "string",
    businessSlug: "string",
    apiUrl: "string",
    primaryColor: "string",
    theme: "string",
    widgetBaseUrl: "string",
    gtagConversionId: "string",
    gtagConversionLabel: "string",
  },
  shadow: "open",
});

// Calendar visibility web component with CSS injection and theme loading
class BaluuCalendar extends (CalendarVisibilityWebComponent as unknown as {
  new (): CustomElementWithLifecycle;
}) {
  connectedCallback() {
    try {
      const parent = Object.getPrototypeOf(Object.getPrototypeOf(this));
      if (parent?.connectedCallback) {
        parent.connectedCallback.call(this);
      }

      injectDocumentLevelRules();
      injectCssIntoShadow(this);

      loadThemeWithFlashGuard(this, "Baluu Calendar");
    } catch (error) {
      console.error("[Baluu Calendar] Error in connectedCallback:", error);
      const container = this.shadowRoot || this;
      const errorDiv = document.createElement("div");
      errorDiv.style.cssText =
        "padding: 20px; text-align: center; color: #dc2626; background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; margin: 20px;";
      errorDiv.innerHTML = `
        <strong>Widget Error</strong><br>
        <small>Failed to initialize. Please refresh the page or contact support.</small>
      `;
      container.appendChild(errorDiv);
    }
  }
}

// Wrapper component that provides React Query context for Basket
function BasketWithProviders(props: BasketProps) {
  const queryClient = getQueryClient();

  return React.createElement(
    QueryClientProvider,
    { client: queryClient },
    React.createElement(Basket, props),
  );
}

// Create the basket web component
const BasketWebComponent = r2wc(withHostBrand(BasketWithProviders), {
  props: {
    businessId: "string",
    apiUrl: "string",
    primaryColor: "string",
    gtagConversionId: "string",
    gtagConversionLabel: "string",
    // "none": the page shows its own basket button (openBasket).
    badge: "string",
  },
  shadow: "open",
});

// Basket web component with CSS injection and theme loading
class BaluuBasket extends (BasketWebComponent as unknown as {
  new (): CustomElementWithLifecycle;
}) {
  connectedCallback() {
    try {
      const parent = Object.getPrototypeOf(Object.getPrototypeOf(this));
      if (parent?.connectedCallback) {
        parent.connectedCallback.call(this);
      }

      injectDocumentLevelRules();
      injectCssIntoShadow(this);

      loadThemeWithFlashGuard(this, "Baluu Basket");
    } catch (error) {
      console.error("[Baluu Basket] Error in connectedCallback:", error);
      const container = this.shadowRoot || this;
      const errorDiv = document.createElement("div");
      errorDiv.style.cssText =
        "padding: 20px; text-align: center; color: #dc2626; background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; margin: 20px;";
      errorDiv.innerHTML = `
        <strong>Widget Error</strong><br>
        <small>Failed to initialize. Please refresh the page or contact support.</small>
      `;
      container.appendChild(errorDiv);
    }
  }
}

// Create the basket checkout overlay web component
const BasketCheckoutOverlayWebComponent = r2wc(withHostBrand(BasketCheckoutOverlay), {
  props: {
    businessId: "string",
    apiUrl: "string",
    primaryColor: "string",
    gtagConversionId: "string",
    gtagConversionLabel: "string",
    paymentReturn: "json",
  },
  shadow: "open",
});

// Basket checkout overlay web component with CSS injection and full-screen overlay styling
class BaluuBasketCheckoutOverlay extends (BasketCheckoutOverlayWebComponent as unknown as {
  new (): CustomElementWithLifecycle;
}) {
  connectedCallback() {
    try {
      const parent = Object.getPrototypeOf(Object.getPrototypeOf(this));
      if (parent?.connectedCallback) {
        parent.connectedCallback.call(this);
      }

      injectDocumentLevelRules();

      this.style.cssText = `
        display: block;
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        width: 100%;
        height: 100%;
        z-index: 9999;
      `;

      injectCssIntoShadow(this);

      loadThemeWithFlashGuard(this, "Baluu Basket Checkout Overlay");
    } catch (error) {
      console.error(
        "[Baluu Basket Checkout Overlay] Error in connectedCallback:",
        error,
      );
      const container = this.shadowRoot || this;
      const errorDiv = document.createElement("div");
      errorDiv.style.cssText =
        "padding: 20px; text-align: center; color: #dc2626; background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; margin: 20px;";
      errorDiv.innerHTML = `
        <strong>Widget Error</strong><br>
        <small>Failed to initialize. Please refresh the page or contact support.</small>
      `;
      container.appendChild(errorDiv);
    }
  }
}

// Custom element for the chat widget
class BaluuChat extends (ChatWebComponent as unknown as {
  new (): CustomElementWithLifecycle;
}) {
  connectedCallback() {
    try {
      const parent = Object.getPrototypeOf(Object.getPrototypeOf(this));
      if (parent?.connectedCallback) {
        parent.connectedCallback.call(this);
      }

      injectDocumentLevelRules();
      injectCssIntoShadow(this);

      loadThemeWithFlashGuard(this, "Baluu Chat");
    } catch (error) {
      console.error("[Baluu Chat] Error in connectedCallback:", error);
    }
  }
}

// Login button web component with CSS injection and theme loading
class BaluuLogin extends (LoginWebComponent as unknown as {
  new (): CustomElementWithLifecycle;
}) {
  connectedCallback() {
    try {
      // Call parent's connectedCallback to create shadowRoot and mount React
      const parent = Object.getPrototypeOf(Object.getPrototypeOf(this));
      if (parent?.connectedCallback) {
        parent.connectedCallback.call(this);
      }

      // Inject fonts into document head (only once, for all components)
      injectDocumentLevelRules();

      // Inject CSS into shadow DOM
      injectCssIntoShadow(this);

      // Fetch and apply theme overrides (holds first paint on first visit)
      loadThemeWithFlashGuard(this, "Baluu Login");
    } catch (error) {
      console.error("[Baluu Login] Error in connectedCallback:", error);
      // Fallback: show error in shadowRoot if exists, otherwise light DOM
      const container = this.shadowRoot || this;
      const errorDiv = document.createElement("div");
      errorDiv.style.cssText =
        "padding: 20px; text-align: center; color: #dc2626; background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; margin: 20px;";
      errorDiv.innerHTML = `
        <strong>Widget Error</strong><br>
        <small>Failed to initialize. Please refresh the page or contact support.</small>
      `;
      container.appendChild(errorDiv);
    }
  }
}

// Profile widget web component with CSS injection and theme loading.
// Deliberately mirrors BaluuBookNow's connectedCallback (inline widget), NOT
// BaluuCheckoutOverlay's (which additionally styles the host as a fixed
// full-viewport box) — Profile/ProfileOverlay.tsx renders its own full-screen
// backdrop via useShadowPortal instead, so the <baluu-profile-overlay> host
// itself stays a normal (unstyled) element. See ProfileOverlay.tsx's top
// comment for why.
class BaluuProfile extends (ProfileWebComponent as unknown as {
  new (): CustomElementWithLifecycle;
}) {
  connectedCallback() {
    try {
      const parent = Object.getPrototypeOf(Object.getPrototypeOf(this));
      if (parent?.connectedCallback) {
        parent.connectedCallback.call(this);
      }

      injectDocumentLevelRules();
      injectCssIntoShadow(this);

      loadThemeWithFlashGuard(this, "Baluu Profile");
    } catch (error) {
      console.error("[Baluu Profile] Error in connectedCallback:", error);
      const container = this.shadowRoot || this;
      const errorDiv = document.createElement("div");
      errorDiv.style.cssText =
        "padding: 20px; text-align: center; color: #dc2626; background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; margin: 20px;";
      errorDiv.innerHTML = `
        <strong>Widget Error</strong><br>
        <small>Failed to initialize. Please refresh the page or contact support.</small>
      `;
      container.appendChild(errorDiv);
    }
  }
}

// Profile overlay web component with CSS injection and theme loading (see
// BaluuProfile's comment above — same reasoning applies here).
class BaluuProfileOverlay extends (ProfileOverlayWebComponent as unknown as {
  new (): CustomElementWithLifecycle;
}) {
  connectedCallback() {
    try {
      const parent = Object.getPrototypeOf(Object.getPrototypeOf(this));
      if (parent?.connectedCallback) {
        parent.connectedCallback.call(this);
      }

      injectDocumentLevelRules();
      injectCssIntoShadow(this);

      loadThemeWithFlashGuard(this, "Baluu Profile Overlay");
    } catch (error) {
      console.error("[Baluu Profile Overlay] Error in connectedCallback:", error);
      const container = this.shadowRoot || this;
      const errorDiv = document.createElement("div");
      errorDiv.style.cssText =
        "padding: 20px; text-align: center; color: #dc2626; background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; margin: 20px;";
      errorDiv.innerHTML = `
        <strong>Widget Error</strong><br>
        <small>Failed to initialize. Please refresh the page or contact support.</small>
      `;
      container.appendChild(errorDiv);
    }
  }
}

// The booking pages a customer reaches from their emails and account
// (manage, cancel, reschedule, review, bundle dates, a bundle, flexitime
// confirmations), one element with a `view`; Website v2 mounts it at v1's
// /bookings/… and /bundles/manage/… addresses
// (plans/websitev2-parity-m3-booking-management.md). Inline, like Profile.
function BookingWithProviders(props: BookingProps) {
  return React.createElement(QueryClientProvider, { client: getQueryClient() }, React.createElement(Booking, props));
}

const BookingWebComponent = r2wc(withHostBrand(BookingWithProviders), {
  props: {
    businessId: "string",
    businessSlug: "string",
    apiUrl: "string",
    widgetBaseUrl: "string",
    linkQuery: "string",
    marketplaceUrl: "string",
    theme: "string",
    primaryColor: "string",
    view: "string",
    bookingId: "string",
    bundleId: "string",
    verb: "string",
    date: "string",
    recordId: "string",
    instalment: "string",
  },
  shadow: "open",
});

class BaluuBooking extends (BookingWebComponent as unknown as {
  new (): CustomElementWithLifecycle;
}) {
  connectedCallback() {
    try {
      const parent = Object.getPrototypeOf(Object.getPrototypeOf(this));
      if (parent?.connectedCallback) {
        parent.connectedCallback.call(this);
      }

      injectDocumentLevelRules();
      injectCssIntoShadow(this);

      loadThemeWithFlashGuard(this, "Baluu Booking");
    } catch (error) {
      console.error("[Baluu Booking] Error in connectedCallback:", error);
      const container = this.shadowRoot || this;
      const errorDiv = document.createElement("div");
      errorDiv.style.cssText =
        "padding: 20px; text-align: center; color: #dc2626; background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; margin: 20px;";
      errorDiv.innerHTML = `
        <strong>Widget Error</strong><br>
        <small>Failed to initialize. Please refresh the page or contact support.</small>
      `;
      container.appendChild(errorDiv);
    }
  }
}

// Buying a class pass, joining a membership and buying a product in the
// page (Website v2's in-page purchases; plans/websitev2-booking-gaps.md B2).
// Each is a full-screen overlay like the checkout: the host element is a
// fixed box over the page, its shadow root gets the widgets' CSS and the
// school's theme (by business-slug), and a pass or product goes on to the
// basket checkout overlay for payment.
const PURCHASE_PROPS = {
  businessId: "string",
  businessSlug: "string",
  apiUrl: "string",
  primaryColor: "string",
} as const;

const PassCheckoutOverlayWebComponent = r2wc(withHostBrand(PassCheckoutOverlay), {
  props: { ...PURCHASE_PROPS, passId: "string", basket: "string" },
  shadow: "open",
});
const MembershipCheckoutOverlayWebComponent = r2wc(withHostBrand(MembershipCheckoutOverlay), {
  props: { ...PURCHASE_PROPS, planId: "string", result: "string" },
  shadow: "open",
});
const ProductCheckoutOverlayWebComponent = r2wc(withHostBrand(ProductCheckoutOverlay), {
  props: { ...PURCHASE_PROPS, productSlug: "string", basket: "string" },
  shadow: "open",
});

/** A purchase overlay's custom element: BaluuCheckoutOverlay's set-up. */
function purchaseOverlayElement(base: unknown, logLabel: string) {
  return class extends (base as { new (): CustomElementWithLifecycle }) {
    connectedCallback() {
      try {
        const parent = Object.getPrototypeOf(Object.getPrototypeOf(this));
        if (parent?.connectedCallback) {
          parent.connectedCallback.call(this);
        }

        injectDocumentLevelRules();

        this.style.cssText = `
          display: block;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          width: 100%;
          height: 100%;
          z-index: 9999;
        `;

        injectCssIntoShadow(this);
        loadThemeWithFlashGuard(this, logLabel);
      } catch (error) {
        console.error(`[${logLabel}] Error in connectedCallback:`, error);
        const container = this.shadowRoot || this;
        const errorDiv = document.createElement("div");
        errorDiv.style.cssText =
          "padding: 20px; text-align: center; color: #dc2626; background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; margin: 20px;";
        errorDiv.innerHTML = `
          <strong>Widget Error</strong><br>
          <small>Failed to initialize. Please refresh the page or contact support.</small>
        `;
        container.appendChild(errorDiv);
      }
    }
  };
}

const BaluuPassCheckoutOverlay = purchaseOverlayElement(PassCheckoutOverlayWebComponent, "Baluu Pass Checkout Overlay");
const BaluuMembershipCheckoutOverlay = purchaseOverlayElement(
  MembershipCheckoutOverlayWebComponent,
  "Baluu Membership Checkout Overlay",
);
const BaluuProductCheckoutOverlay = purchaseOverlayElement(
  ProductCheckoutOverlayWebComponent,
  "Baluu Product Checkout Overlay",
);

// Register the custom elements with error handling
if (typeof window !== "undefined") {
  try {
    if (!customElements.get("baluu-book-now")) {
      customElements.define("baluu-book-now", BaluuBookNow);
    }
    if (!customElements.get("baluu-checkout-overlay")) {
      customElements.define("baluu-checkout-overlay", BaluuCheckoutOverlay);
    }
    if (!customElements.get("baluu-timetable")) {
      customElements.define("baluu-timetable", BaluuTimetable);
    }
    if (!customElements.get("baluu-timetable-overlay")) {
      customElements.define("baluu-timetable-overlay", BaluuTimetableOverlay);
    }
    if (!customElements.get("baluu-whats-on")) {
      customElements.define("baluu-whats-on", BaluuWhatsOn);
    }
    if (!customElements.get("baluu-giftcard-buy-now")) {
      customElements.define("baluu-giftcard-buy-now", BaluuGiftcardBuyNow);
    }
    if (!customElements.get("baluu-giftcard-checkout-overlay")) {
      customElements.define("baluu-giftcard-checkout-overlay", BaluuGiftcardCheckoutOverlay);
    }
    if (!customElements.get("baluu-calendar")) {
      customElements.define("baluu-calendar", BaluuCalendar);
    }
    if (!customElements.get("baluu-basket")) {
      customElements.define("baluu-basket", BaluuBasket);
    }
    if (!customElements.get("baluu-basket-checkout-overlay")) {
      customElements.define("baluu-basket-checkout-overlay", BaluuBasketCheckoutOverlay);
    }
    if (!customElements.get("baluu-chat")) {
      customElements.define("baluu-chat", BaluuChat);
    }
    if (!customElements.get("baluu-login")) {
      customElements.define("baluu-login", BaluuLogin);
    }
    if (!customElements.get("baluu-profile")) {
      customElements.define("baluu-profile", BaluuProfile);
    }
    if (!customElements.get("baluu-profile-overlay")) {
      customElements.define("baluu-profile-overlay", BaluuProfileOverlay);
    }
    if (!customElements.get("baluu-booking")) {
      customElements.define("baluu-booking", BaluuBooking);
    }
    if (!customElements.get("baluu-pass-checkout-overlay")) {
      customElements.define("baluu-pass-checkout-overlay", BaluuPassCheckoutOverlay);
    }
    if (!customElements.get("baluu-membership-checkout-overlay")) {
      customElements.define("baluu-membership-checkout-overlay", BaluuMembershipCheckoutOverlay);
    }
    if (!customElements.get("baluu-product-checkout-overlay")) {
      customElements.define("baluu-product-checkout-overlay", BaluuProductCheckoutOverlay);
    }

    // Signal successful registration (useful for debugging)
    if (typeof console !== "undefined") {
      console.debug("[Baluu Widgets] Custom elements registered successfully");
    }
  } catch (error) {
    // Log registration errors but don't throw to avoid breaking page
    if (typeof console !== "undefined") {
      console.error(
        "[Baluu Widgets] Failed to register custom elements:",
        error,
      );
    }
  }
}

// Back from Stripe's Checkout with a new membership (`?baluu_membership=
// success`): its confirmation, on whatever page loaded the components.
if (typeof window !== "undefined") {
  const resume = () => {
    try {
      void resumeMembershipReturn().catch((error) => {
        console.error("[Baluu Widgets] Couldn't show the membership confirmation:", error);
      });
    } catch (error) {
      console.error("[Baluu Widgets] Couldn't show the membership confirmation:", error);
    }
  };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", resume, { once: true });
  else resume();
}

/**
 * An attribute of the school's own Baluu element on this page, for a return
 * whose kept settings are gone (no storage): the api-url the page's embed
 * was configured with beats the production default.
 */
function pageElementAttribute(businessId: string, attribute: string): string | undefined {
  for (const el of Array.from(document.querySelectorAll(`[business-id][${attribute}]`))) {
    if (el.getAttribute("business-id") === businessId) return el.getAttribute(attribute) ?? undefined;
  }
  return undefined;
}

// Back from a Stripe redirect taken at the basket checkout's card step
// (3-D Secure off-page, bank redirects): the checkout opens to finish that
// order, on whatever page loaded the components.
if (typeof window !== "undefined") {
  const resumePayment = () => {
    try {
      resumeBasketPaymentReturn((ret: BasketPaymentReturnProp) => {
        const ctx = ret.context;
        openBasketCheckout({
          businessId: ret.businessId,
          apiUrl: ctx?.apiUrl ?? pageElementAttribute(ret.businessId, "api-url"),
          primaryColor: ctx?.primaryColor ?? pageElementAttribute(ret.businessId, "primary-color"),
          gtagConversionId: ctx?.gtagConversionId,
          gtagConversionLabel: ctx?.gtagConversionLabel,
          paymentReturn: ret,
        });
      });
    } catch (error) {
      console.error("[Baluu Widgets] Couldn't finish the payment return:", error);
    }
  };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", resumePayment, { once: true });
  else resumePayment();
}

// Export for programmatic usage
export { BookNowButton, BookNowButtonWithProviders };
export type { BookNowButtonProps };
export { Timetable, TimetableWithProviders };
export type { TimetableProps };
export { TimetableOverlay, TimetableOverlayWithProviders };
export type { TimetableOverlayProps };
export { WhatsOn, WhatsOnWithProviders };
export type { WhatsOnProps };
export { GiftcardBuyNowButton, GiftcardBuyNowButtonWithProviders };
export type { GiftcardBuyNowButtonProps };
export { CalendarVisibility, CalendarVisibilityWithProviders };
export type { CalendarVisibilityProps };
export { Basket, BasketWithProviders };
export type { BasketProps };
export { BasketCheckoutOverlay };
export { Chat };
export type { ChatProps };

// Also expose on window for UMD builds
if (typeof window !== "undefined") {
  (window as unknown as { BaluuWidgets: unknown }).BaluuWidgets = {
    BookNowButton,
    BookNowButtonWithProviders,
    Timetable,
    TimetableWithProviders,
    TimetableOverlay,
    TimetableOverlayWithProviders,
    WhatsOn,
    WhatsOnWithProviders,
    GiftcardBuyNowButton,
    GiftcardBuyNowButtonWithProviders,
    CalendarVisibility,
    CalendarVisibilityWithProviders,
    Basket,
    BasketWithProviders,
    BasketCheckoutOverlay,
    Chat,
    // Open a gift card / pass / membership / product purchase on the page:
    // openPurchase("product", { businessId, businessSlug, productSlug }).
    openPurchase: openPurchaseOverlay,
    // Open the basket's drawer from the page's own basket button (a page
    // whose overlays have `basket="page"`): openBasket({ businessId, apiUrl }).
    openBasket,
    // Helper to manually render a React component
    render: (
      container: HTMLElement,
      component: React.ComponentType<unknown>,
      props: Record<string, unknown>,
    ) => {
      const queryClient = getQueryClient();
      const root = ReactDOM.createRoot(container);
      root.render(
        React.createElement(
          QueryClientProvider,
          { client: queryClient },
          React.createElement(component, props),
        ),
      );
      return root;
    },
  };
}
